import React, { useEffect, useState } from "react";
import { Grid, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiSmartphone } from "react-icons/fi";
import { QRCodeCanvas } from "qrcode.react";

import { VBabyColors } from "../../colors";
import {
  Body,
  Card,
  EmotionPanel,
  IconCircle,
  PanelSubtitle,
  PanelTitle,
  Shell,
} from "../../components/vbaby";
import {
  Container,
  ContainerLogo,
  Divider,
  Footer,
  Header,
  Main,
  PoliciesContainer,
  QrFrame,
  QrHint,
  SelectLanguageButtonContainer,
  SelectLanguageContainer,
  StepList,
  StepNumber,
  StepRow,
  StepText,
  StoreBadge,
  TextButton,
} from "./style";
import { getS3LogoUrl } from "../../utils/s3";
import AppleStoreLogo from "../../assets/apple_store_logo.png";
import PlayStoreLogo from "../../assets/play_store_logo.png";

const URL_PLAY_STORE =
  "https://play.google.com/store/apps/details?id=com.vlab.vbaby&utm_source=vbaby-assista&utm_content=buttom";
const URL_APPLE_STORE =
  "https://apps.apple.com/app/apple-store/id1583958416?pt=123391444&ct=vbaby-assista&mt=8";

const QR_SIZE = 74;

const selectedLanguageStyle = {
  color: VBabyColors.primaryDark,
  fontWeight: 600,
};

const DownloadApp = () => {
  const { t, i18n } = useTranslation();
  const [setting, setSetting] = useState({ logoUrl: "" });

  useEffect(() => {
    getLogoUrl();
  }, []);

  const getLogoUrl = async () => {
    const logoUrl = await getS3LogoUrl();
    setSetting({ logoUrl });
  };

  const openStore = (url) => {
    window.dataLayer?.push({ event: "go-to-app" });
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleSupportMessage = (supportMessage) => {
    const phoneNumber = "551124245299";
    let message = "";
    if (supportMessage) {
      message = t("supportMessages.messageViewUser");
    } else {
      message = t("supportMessages.messageHelp");
    }

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}/?text=${encodedMessage}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
    mixpanel.track("CALL_SUPPORT_SOLUTION_ASSISTA", {
      distinct_id: "",
      CALL_SUPPORT: true,
      PHONE_SUPPORT: phoneNumber,
      URL_SUPPORT: whatsappUrl,
      MESSAGE_SUPPORT: message,
    });
  };

  const steps = [
    t("downloadApp.stepStore"),
    t("downloadApp.stepPhone"),
    t("downloadApp.stepWatch"),
  ];

  return (
    <Container>
      <Header>
        <ContainerLogo backgroundImage={`url(${setting.logoUrl})`} />
      </Header>

      <Main>
        <Shell>
          <Card>
            <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }}>
              {/* Ícone neutro porque o login também chega aqui, sem ter pagado. */}
              <EmotionPanel>
                <IconCircle aria-hidden="true">
                  <FiSmartphone size={24} />
                </IconCircle>
                <PanelTitle>{t("downloadApp.title")}</PanelTitle>
                <PanelSubtitle>{t("downloadApp.subtitle")}</PanelSubtitle>
              </EmotionPanel>

              <Body justifyContent="center">
                <StepList>
                  {steps.map((step, index) => (
                    <StepRow key={step}>
                      <StepNumber aria-hidden="true">{index + 1}</StepNumber>
                      <StepText>{step}</StepText>
                    </StepRow>
                  ))}
                </StepList>

                <StoreBadge
                  type="button"
                  onClick={() => openStore(URL_APPLE_STORE)}
                >
                  <img src={AppleStoreLogo} alt="App Store" />
                </StoreBadge>
                <StoreBadge
                  type="button"
                  onClick={() => openStore(URL_PLAY_STORE)}
                >
                  <img src={PlayStoreLogo} alt="Google Play" />
                </StoreBadge>

                {/* Dois códigos: não existe link único que resolva a loja por
                    sistema. */}
                <QrHint>
                  <QrFrame>
                    <QRCodeCanvas value={URL_APPLE_STORE} size={QR_SIZE} />
                    App Store
                  </QrFrame>
                  <QrFrame>
                    <QRCodeCanvas value={URL_PLAY_STORE} size={QR_SIZE} />
                    Google Play
                  </QrFrame>
                  <Text
                    margin={0}
                    fontFamily="body"
                    fontSize="12px"
                    lineHeight="18px"
                    color="vbaby.text2"
                  >
                    {t("downloadApp.scanToDownload")}
                  </Text>
                </QrHint>
              </Body>
            </Grid>
          </Card>
        </Shell>
      </Main>

      <Footer>
        <TextButton onClick={() => handleSupportMessage(true)}>
          {t("buttons.help")}
        </TextButton>

        <SelectLanguageContainer>
          <span className="selected-language">{t("changeLan.label")}</span>

          <SelectLanguageButtonContainer>
            <button
              className="selected-language-button"
              style={i18n.language === "pt-BR" ? selectedLanguageStyle : {}}
              onClick={() => {
                i18n.changeLanguage("pt-BR");
              }}
            >
              Português
            </button>

            <button
              className="selected-language-button"
              style={i18n.language === "en-US" ? selectedLanguageStyle : {}}
              onClick={() => {
                i18n.changeLanguage("en-US");
              }}
            >
              English
            </button>

            <button
              className="selected-language-button"
              style={i18n.language === "es" ? selectedLanguageStyle : {}}
              onClick={() => {
                i18n.changeLanguage("es");
              }}
            >
              Español
            </button>
          </SelectLanguageButtonContainer>
        </SelectLanguageContainer>

        <Divider />

        <PoliciesContainer>
          <a
            href="https://documentos.vlabhealth.com/POLITICADEPRIVACIDADE.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="terms-link"
          >
            {`${t("terms.privacyPolitics")}`}
          </a>

          <a
            href="https://documentos.vlabhealth.com/TermosUsoConvidados.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="terms-link"
          >
            {`${t("terms.termsOfUse")}`}
          </a>

          <a
            href="https://documentos.vlabhealth.com/TermoDeColetaDeDadosDeSaúde.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="terms-link"
          >
            {`${t("terms.healthTerms")}`}
          </a>
        </PoliciesContainer>
      </Footer>
    </Container>
  );
};

export default DownloadApp;
